import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter, map, startWith } from "rxjs/operators";

import { WorkflowContextBarService } from "../../services/workflow-context-bar.service";
import { WorkflowLedgerComponent } from "../workflow-ledger/workflow-ledger.component";
import { WorkflowSummaryBarComponent } from "../workflow-ledger/workflow-summary-bar.component";

/** Route data flag: the page renders its own hearing header, so the app-wide summary bar stays out (#1530). */
export const HEARING_HEADER_ROUTE_DATA = "hearingHeader";

/** True when any route on the activated branch carries `hearingHeader: true`. */
export function routeCarriesHearingHeader(root: ActivatedRouteSnapshot | null | undefined): boolean {
  let current: ActivatedRouteSnapshot | null = root ?? null;
  while (current) {
    if (current.data?.[HEARING_HEADER_ROUTE_DATA] === true) {
      return true;
    }
    current = current.firstChild;
  }
  return false;
}

@Component({
  selector: "phms-workflow-context-bar-host",
  imports: [WorkflowLedgerComponent, WorkflowSummaryBarComponent],
  templateUrl: "./workflow-context-bar-host.component.html",
  styleUrl: "./workflow-context-bar-host.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowContextBarHostComponent {
  private readonly contextBar = inject(WorkflowContextBarService);
  private readonly router = inject(Router);

  protected readonly context = this.contextBar.context;
  protected readonly announcement = signal("");
  /** The hearing pages carry their own header in place of this bar (#1530). */
  protected readonly hearingHeaderActive = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => routeCarriesHearingHeader(this.router.routerState.snapshot.root)),
      startWith(routeCarriesHearingHeader(this.router.routerState.snapshot.root)),
    ),
    { initialValue: false },
  );

  protected ledgerRegionId(token: string): string {
    return `${token}-ledger`;
  }

  protected setExpanded(token: string, expanded: boolean): void {
    if (!this.contextBar.setExpanded(token, expanded)) {
      return;
    }

    this.announcement.set(`Workflow history ${expanded ? "expanded" : "collapsed"}.`);
  }
}
